import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Checkbox,
  Tooltip,
  IconButton,
  TablePagination,
  LinearProgress,
  Skeleton
} from '@mui/material'
import {
  TravelExplore,
  PersonSearch,
  AlternateEmail,
  MailOutline,
  Download
} from '@mui/icons-material'
import ExcelIcon from '../../../../../components/icon/ExcelIcon'
import DartIcon from '../atoms/DartIcon'
import CampaignFirmRow from './CampaignFirmRow'
import CampaignFilterToolbar from '../molecules/CampaignFilterToolbar'
import CampaignProgressBars from './CampaignActionCards'

const TOUR_EXPAND_STEPS = [3, 5, 6, 7]

const BULK_ACTIONS = [
  { key: 'checkRelevancy', icon: DartIcon, labelKey: 'mailTemplate.campaigns.dashboard.relevancyCheck', color: '#7C4DFF' },
  { key: 'searchContacts', icon: PersonSearch, labelKey: 'mailTemplate.campaigns.dashboard.contactSearch', color: '#0097A7' },
  { key: 'queryEmailAddresses', icon: AlternateEmail, labelKey: 'mailTemplate.campaigns.dashboard.emailAddressQuery', color: '#E65100' },
  { key: 'generateContent', icon: MailOutline, labelKey: 'mailTemplate.campaigns.dashboard.generateContent', color: '#2E7D32' }
]

const SelectionActions = ({ selectedCount, onBulkAction }) => {
  const { t } = useTranslation()
  const detail = selectedCount > 0
    ? t('mailTemplate.campaigns.dashboard.firmsCount', { count: selectedCount })
    : t('mailTemplate.campaigns.dashboard.allFirms')

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
      {BULK_ACTIONS.map(({ key, icon: Icon, labelKey, color }) => {
        const label = t(labelKey)
        return (
          <Tooltip key={key} title={`${label} (${detail})`} arrow placement="top">
            <Box
              data-tour={`campaign-action-${key}`}
              onClick={() => onBulkAction?.(key)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                px: 1.25,
                py: 0.625,
                borderRadius: '8px',
                cursor: 'pointer',
                color,
                backgroundColor: `${color}10`,
                border: '1px solid',
                borderColor: `${color}25`,
                transition: 'all 0.15s',
                '&:hover': {
                  backgroundColor: `${color}22`,
                  borderColor: `${color}50`
                }
              }}
            >
              <Icon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap', display: { xs: 'none', lg: 'block' } }}>
                {label}
              </Typography>
            </Box>
          </Tooltip>
        )
      })}
    </Box>
  )
}

const LoadingSkeleton = () => (
  <Box>
    {Array.from({ length: 10 }, (_, i) => i + 1).map((i) => (
      <Box key={i} sx={{ px: 2, py: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="rounded" width={20} height={20} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="40%" height={22} />
            <Skeleton variant="text" width="60%" height={16} sx={{ mt: 0.5 }} />
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: '6px' }} />
              <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: '6px' }} />
              <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: '6px' }} />
            </Box>
          </Box>
        </Box>
      </Box>
    ))}
  </Box>
)

const EmptyState = () => {
  const { t } = useTranslation()
  return (
    <Box sx={{ py: 8, textAlign: 'center' }}>
      <TravelExplore sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
      <Typography variant="body1" color="text.secondary">
        {t('mailTemplate.campaigns.dashboard.noFirmsMatch')}
      </Typography>
    </Box>
  )
}

const CampaignDashboardTable = ({
  firms = [],
  loading = false,
  selectedFirmIds = {},
  selectAllMode,
  excludedCount = 0,
  onToggleFirmSelect,
  onBulkAction,
  onContactToggle,
  onPremiumContactToggle,
  page = 0,
  pageSize = 10,
  totalCount = 0,
  onPageChange,
  onPageSizeChange,
  tasks,
  onTaskCancel,
  onEmailPreview,
  onSelectAllItems,
  onClearSelection,
  filters,
  onFilterChange,
  sorting,
  onSortChange,
  onDeleteFirm,
  onDownloadExcel,
  tourStepIndex
}) => {
  const { t } = useTranslation()

  const [expandedFirmId, setExpandedFirmId] = useState(null)
  const handleToggleExpand = useCallback(
    (firmId) => {
      setExpandedFirmId((prev) => (prev === firmId ? null : firmId))
    },
    []
  )

  useEffect(() => {
    if (TOUR_EXPAND_STEPS.includes(tourStepIndex) && firms.length > 0) {
      setExpandedFirmId(firms[0].id)
    } else if (tourStepIndex >= 0 && !TOUR_EXPAND_STEPS.includes(tourStepIndex)) {
      setExpandedFirmId(null)
    }
  }, [tourStepIndex, firms])

  const allPageSelected = useMemo(() => {
    if (!firms.length) return false
    return firms.every((f) => selectedFirmIds[f.id])
  }, [firms, selectedFirmIds])

  const allSelected = selectAllMode === 'all' || allPageSelected

  const handleToggleAll = useCallback(() => {
    if (!firms.length) return
    if (allSelected) {
      if (onClearSelection) onClearSelection()
    } else {
      firms.forEach((f) => onToggleFirmSelect?.(f.id, true))
    }
  }, [firms, allSelected, onToggleFirmSelect, onClearSelection])

  const pageSelectedCount = useMemo(() => {
    return firms.filter((f) => selectedFirmIds[f.id]).length
  }, [firms, selectedFirmIds])

  const someSelected = selectAllMode !== 'all' && pageSelectedCount > 0 && pageSelectedCount < firms.length

  const totalSelectedCount = useMemo(() => {
    return Object.values(selectedFirmIds).filter(Boolean).length
  }, [selectedFirmIds])

  const selectedCount = useMemo(() => {
    if (selectAllMode === 'all') return totalCount
    if (selectAllMode === 'allExcept') return totalCount - excludedCount
    return totalSelectedCount
  }, [selectAllMode, totalCount, excludedCount, totalSelectedCount])

  return (
    <Box>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: 'background.paper'
        }}
      >
      <CampaignFilterToolbar filters={filters} onFilterChange={onFilterChange} sorting={sorting} onSortChange={onSortChange} />
      <CampaignProgressBars tasks={tasks} onCancel={onTaskCancel} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'rgba(0, 0, 0, 0.02)'
        }}
      >
        <Box sx={{ width: 32 }} />
        <Checkbox
          size="small"
          checked={allSelected}
          indeterminate={someSelected}
          onChange={handleToggleAll}
          sx={{ p: 0.5, flexShrink: 0 }}
        />
        <Typography
          variant="caption"
          sx={{
            flex: 1,
            fontWeight: 600,
            letterSpacing: 0.5,
            color: 'text.secondary'
          }}
        >
          {t('mailTemplate.campaigns.dashboard.firmsAndContacts')}
        </Typography>
        <SelectionActions selectedCount={selectedCount} onBulkAction={onBulkAction} />
        <Tooltip title={t('mailTemplate.campaigns.dashboard.downloadExcel')} arrow placement="top">
          <IconButton
            size="small"
            onClick={onDownloadExcel}
            sx={{
              p: 0.5,
              borderRadius: '8px',
              color: '#1B6B34',
              '&:hover': { color: '#145226', backgroundColor: 'rgba(27, 107, 52, 0.08)' }
            }}
          >
            <ExcelIcon width={18} height={18} color="currentColor" />
            <Download sx={{ fontSize: 14, ml: -0.25 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {allPageSelected && totalCount > firms.length && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            py: 0.75,
            backgroundColor: 'rgba(25, 118, 210, 0.06)',
            borderBottom: '1px solid',
            borderColor: 'divider',
            fontSize: '0.8rem'
          }}
        >
          {selectAllMode === 'all'
            ? (
              <>
                <Typography variant="caption" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                  {t('mailTemplate.campaigns.dashboard.allFirmsSelected', { count: totalCount })}
                </Typography>
                <Typography
                  variant="caption"
                  onClick={onClearSelection}
                  sx={{ fontSize: '0.8rem', color: 'primary.main', cursor: 'pointer', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                >
                  {t('mailTemplate.campaigns.dashboard.clearSelection')}
                </Typography>
              </>
              )
            : (
              <>
                <Typography variant="caption" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                  {t('mailTemplate.campaigns.dashboard.allPageSelected', { count: firms.length })}
                </Typography>
                <Typography
                  variant="caption"
                  onClick={onSelectAllItems}
                  sx={{ fontSize: '0.8rem', color: 'primary.main', cursor: 'pointer', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                >
                  {t('mailTemplate.campaigns.dashboard.selectAllFirms', { count: totalCount })}
                </Typography>
              </>
              )
          }
        </Box>
      )}

      {loading && <LinearProgress sx={{ height: 2 }} />}

      {loading
        ? <LoadingSkeleton />
        : firms.length === 0
          ? <EmptyState />
          : firms.map((firm, index) => (
            <CampaignFirmRow
              key={firm.id}
              firm={firm}
              isFirstRow={index === 0}
              isExpanded={expandedFirmId === firm.id}
              isSelected={Boolean(selectedFirmIds[firm.id])}
              onToggleExpand={() => handleToggleExpand(firm.id)}
              onToggleSelect={() => onToggleFirmSelect?.(firm.id)}
              onContactToggle={onContactToggle}
              onPremiumContactToggle={onPremiumContactToggle}
              onEmailPreview={onEmailPreview}
              onDelete={onDeleteFirm}
            />
          ))
      }

      {!loading && firms.length > 0 && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => onPageChange?.(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => onPageSizeChange?.(parseInt(e.target.value, 10))}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage={t('mailTemplate.campaigns.dashboard.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('mailTemplate.campaigns.dashboard.displayedRows', { from, to, count: count !== -1 ? count : `>${to}` })}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            '.MuiTablePagination-toolbar': {
              minHeight: 48,
              px: 2,
              display: 'flex',
              alignItems: 'center'
            },
            '.MuiTablePagination-spacer': {
              display: 'none'
            },
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              fontSize: '0.8125rem',
              m: 0
            },
            '.MuiTablePagination-displayedRows': {
              ml: 'auto'
            },
            '.MuiTablePagination-select': {
              fontSize: '0.8125rem'
            }
          }}
        />
      )}

      </Box>
    </Box>
  )
}

export default CampaignDashboardTable
